import React, { useState, useEffect } from "react";
import { useCookies } from 'react-cookie';
import Sidebar from './SideBar';
import useAuthRedirect from "./Componentes/auth";
import Axios from 'axios';
import config from './Componentes/config';
import Swal from 'sweetalert2';
import { registrarLog } from './Componentes/logService';
import { generarFiniquitoPDF } from "./Documentos/Finiquito";
import { formatRutDV, formatRemuneracion, cleanRemuneracion } from './Validations/TrabajadoresValidation';
import { NumerosALetras } from 'numero-a-letras';
import { getEmpresaByCodigo } from './Componentes/empresasConfig';

const cardImages = {
    DAFA: "/img/dafaLogo.png",
    FUGA: "/img/fugarLogo.png",
    INVE: "/img/idafaLogo.png",
    TRAN: "/img/extasisLogo.png",
    NIDI: "/img/nidiamLogo.png"
};

const buildDefaults = (codigo) => {
    const e = getEmpresaByCodigo(codigo);
    return {
        empresa: e.nombre,
        rutEmpresa: e.rutFormatted,
        representanteLegal: e.representanteLegal,
        rutRepresentante: e.rutRepresentante,
        domicilioEmpresa: e.domicilio,
        comunaEmpresa: (e.comuna || '').toUpperCase(),
        ciudadEmpresa: (e.ciudad || '').toUpperCase(),
    };
};

const articulosFiniquito = [
    "ART. 161 N°1: NECESIDADES DE LA EMPRESA.",
    "ART. 160 N°3: NO CONCURRENCIA A LAS LABORES.",
    "ART. 160 N°4: ABANDONO DEL TRABAJO.",
    "ART. 160 N°7: INCUMPLIMIENTO GRAVE DE LAS OBLIGACIONES QUE IMPONE EL CONTRATO.",
    "ART. 159 N°1: MUTUO ACUERDO DE LAS PARTES.",
    "ART. 159 N°2: RENUNCIA DEL TRABAJADOR.",
    "ART. 159 N°4: VENCIMIENTO DEL PLAZO CONVENIDO EN EL CONTRATO.",
    "ART. 159 N°5: CONCLUSIÓN DEL TRABAJO O SERVICIO QUE DIO ORIGEN AL CONTRATO."
];

// ==================== COMPONENTES REUTILIZABLES ====================
const FormInput = ({ label, field, type = "text", placeholder = "", options = null, value, onChange }) => {
    if (options) {
        return (
            <div className="input-group input-group-sm mb-2">
                <span className="input-group-text" style={{ minWidth: '150px', fontSize: '12px' }}>{label}</span>
                <select className="form-select form-select-sm" value={value} onChange={(e) => onChange(field, e.target.value)}>
                    <option value="">— Seleccione —</option>
                    {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
            </div>
        );
    }
    return (
        <div className="input-group input-group-sm mb-2">
            <span className="input-group-text" style={{ minWidth: '150px', fontSize: '12px' }}>{label}</span>
            <input
                type={type}
                className="form-control form-control-sm"
                value={value || ""}
                onChange={(e) => onChange(field, e.target.value)}
                placeholder={placeholder}
            />
        </div>
    );
};

const AccordionSection = ({ id, title, icon, isOpen, onToggle, children }) => (
    <div className="card mb-2">
        <div
            className="card-header py-2 d-flex align-items-center justify-content-between"
            style={{ cursor: 'pointer', backgroundColor: isOpen ? '#e8f0fe' : '#f8f9fa' }}
            onClick={() => onToggle(id)}
        >
            <h6 className="m-0" style={{ fontSize: '13px', fontWeight: '600' }}>
                <span className="material-symbols-rounded" style={{ fontSize: '16px', verticalAlign: 'middle', marginRight: '6px' }}>{icon}</span>
                {title}
            </h6>
            <span className="material-symbols-rounded" style={{ fontSize: '18px', transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                expand_more
            </span>
        </div>
        <div style={{ maxHeight: isOpen ? '800px' : '0', overflow: 'hidden', transition: 'max-height 0.3s ease-in-out' }}>
            <div className="card-body py-2 px-3">
                {children}
            </div>
        </div>
    </div>
);

// ==================== COMPONENTE PRINCIPAL ====================
function Finiquitos() {
    useAuthRedirect();
    const [cookies] = useCookies(['selectedCard']);
    const imageUrl = cardImages[cookies.selectedCard];
    const selectedCard = cookies.selectedCard;
    const idEmpresa = cookies.idEmpresa;

    const [isComponentsCollapsed, setIsComponentsCollapsed] = useState(true);
    const [isUtilitiesCollapsed, setIsUtilitiesCollapsed] = useState(true);
    const [isInventario, setisIventatioCollapsed] = useState(true);
    const [isPrevencion, setisPrevencionCollapsed] = useState(true);
    const [isLogistica, setisLogisticaCollapsed] = useState(true);
    const [isContabilidadCollapsed, setIsContabilidadCollapsed] = useState(true);
    const [isAdministracionCollapsed, setIsAdministracionCollapsed] = useState(true);
    const toggleCollapse = (setter) => setter(prev => !prev);

    const empresaHeader = getEmpresaByCodigo(selectedCard).nombre;

    const [trabajadores, setTrabajadores] = useState([]);
    const [idTrabajadorSeleccionado, setIdTrabajadorSeleccionado] = useState('');
    const [openSection, setOpenSection] = useState('causal');
    const toggleSection = (s) => setOpenSection(openSection === s ? null : s);

    const defaults = buildDefaults(selectedCard);

    const [formData, setFormData] = useState({
        // Empresa
        empresa: defaults.empresa,
        rutEmpresa: defaults.rutEmpresa,
        representanteLegal: defaults.representanteLegal,
        rutRepresentante: defaults.rutRepresentante,
        domicilioEmpresa: defaults.domicilioEmpresa,
        comunaEmpresa: defaults.comunaEmpresa,
        ciudadEmpresa: defaults.ciudadEmpresa,
        // Trabajador
        nombres: "",
        apellidos: "",
        rutTrabajador: "",
        nacionalidad: "",
        fechaNacimiento: "",
        domicilioTrabajador: "",
        comunaTrabajador: "",
        cargo: "",
        // Finiquito
        articuloFiniquito: "",
        enComuna: "",
        fechaFiniquito: "",
        vigenciaDesde: "",
        vigenciaHasta: "",
        // Montos
        indemnizacion: "",
        vacaciones: "",
        faena: "",
        mesAviso: ""
    });

    const [logoBase64, setLogoBase64] = useState(null);

    useEffect(() => {
        const loadLogo = async () => {
            try {
                const res = await fetch('/img/dafaLogoPDF.png');
                const blob = await res.blob();
                const reader = new FileReader();
                reader.onloadend = () => setLogoBase64(reader.result);
                reader.readAsDataURL(blob);
            } catch (err) {
                console.error('Error cargando logo:', err);
            }
        };
        loadLogo();
    }, []);

    // Actualizar datos de empresa cuando cambia la card
    useEffect(() => {
        const d = buildDefaults(selectedCard);
        setFormData(prev => ({
            ...prev,
            empresa: d.empresa,
            rutEmpresa: d.rutEmpresa,
            representanteLegal: d.representanteLegal,
            rutRepresentante: d.rutRepresentante,
            domicilioEmpresa: d.domicilioEmpresa,
            comunaEmpresa: d.comunaEmpresa,
            ciudadEmpresa: d.ciudadEmpresa,
        }));
    }, [selectedCard]);

    // Cargar trabajadores
    useEffect(() => {
        Axios.get(`${config.API_URL}/CargaTrabajadores?id=${idEmpresa}`)
            .then(res => setTrabajadores(Array.isArray(res.data) ? res.data : []))
            .catch(err => console.error("Error al obtener trabajadores:", err));
    }, [idEmpresa]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    // Al seleccionar trabajador, precargar datos desde backend
    const handleSelectTrabajador = (e) => {
        const id = e.target.value;
        setIdTrabajadorSeleccionado(id);
        if (!id) return;

        Axios.get(`${config.API_URL}/getTrabajadores`, { params: { idTrabajador: id } })
            .then(res => {
                const data = Array.isArray(res.data) ? res.data[0] : res.data;
                if (!data) return;
                setFormData(prev => ({
                    ...prev,
                    nombres: data.Nombres || "",
                    apellidos: data.Apellidos || "",
                    rutTrabajador: formatRutDV(data.Rut) || "",
                    nacionalidad: data.Nacionalidad || "",
                    fechaNacimiento: data.FechaNacimiento ? data.FechaNacimiento.split('T')[0] : "",
                    domicilioTrabajador: data.Domicilio || "",
                    comunaTrabajador: data.Comuna || "",
                    cargo: data.Cargo || 'SIN CARGO',
                    vigenciaDesde: data.VigenciaDesde ? data.VigenciaDesde.replace(/\//g, '-') : "",
                    vigenciaHasta: data.VigenciaHasta ? data.VigenciaHasta.replace(/\//g, '-') : "",
                }));
            })
            .catch(err => console.error("Error al cargar trabajador:", err));
    };

    // ==================== UTILIDADES DE PRESENTACIÓN ====================
    const isEmpty = (v) => !v || String(v).trim() === "";

    const renderText = (value, label) => {
        if (isEmpty(value)) {
            return (
                <span style={{
                    backgroundColor: '#fff3cd',
                    border: '1px dashed #ffc107',
                    padding: '1px 4px',
                    borderRadius: '3px',
                    fontSize: 'inherit'
                }}>[{label}]</span>
            );
        }
        return <span>{value}</span>;
    };

    const formatearFechaTexto = (fechaISO) => {
        if (!fechaISO) return "";
        const meses = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
        const [year, month, day] = fechaISO.split("-").map(Number);
        if (!year || !month || !day) return "";
        return `${day} DE ${meses[month - 1]} DEL ${year}`;
    };

    const fechaDMY = (iso) => (iso ? iso.split("-").reverse().join("-") : "");

    // Total de montos (para mostrar en la vista previa)
    const totalMontos = () => {
        return (
            Number(cleanRemuneracion(formData.indemnizacion) || "0") +
            Number(cleanRemuneracion(formData.vacaciones) || "0") +
            Number(cleanRemuneracion(formData.faena) || "0") +
            Number(cleanRemuneracion(formData.mesAviso) || "0")
        );
    };

    const totalEnLetras = () => {
        const total = totalMontos();
        if (!total) return "";
        let letras = NumerosALetras(total.toString().replace(".", ""));
        letras = letras.replace(/\s\d+\/\d+\sM\.N\.?$/, '');
        return letras;
    };

    const nombreCompleto = () => `${(formData.nombres || '').toUpperCase()} ${(formData.apellidos || '').toUpperCase()}`.trim();

    // Contar campos obligatorios vacíos
    const countEmptyFields = () => {
        const required = ['nombres', 'apellidos', 'rutTrabajador', 'cargo', 'articuloFiniquito',
            'enComuna', 'fechaFiniquito', 'vigenciaDesde', 'vigenciaHasta'];
        return required.filter(f => isEmpty(formData[f])).length;
    };

    // ==================== GENERACIÓN PDF ====================
    const buildPayload = () => ({
        datosTrabajador: {
            Nombres: formData.nombres,
            Apellidos: formData.apellidos,
            Rut: formData.rutTrabajador,
            Nacionalidad: formData.nacionalidad,
            FechaNacimiento: formData.fechaNacimiento,
            Domicilio: formData.domicilioTrabajador,
            Comuna: formData.comunaTrabajador,
            Cargo: formData.cargo,
            EnComuna: formData.enComuna,
            FechaFiniquito: formData.fechaFiniquito,
            Desde: formData.vigenciaDesde,
            Hasta: formData.vigenciaHasta,
            Articulo: formData.articuloFiniquito,
            indemnizacion: formatRemuneracion(formData.indemnizacion),
            Vacaciones: formatRemuneracion(formData.vacaciones),
            Faena: formatRemuneracion(formData.faena),
            MesAviso: formatRemuneracion(formData.mesAviso),
        },
        datosEmpresa: {
            empresa: formData.empresa,
            rutEmpresa: formData.rutEmpresa,
            representanteLegal: formData.representanteLegal,
            rutRepresentante: formData.rutRepresentante,
            domicilioEmpresa: formData.domicilioEmpresa,
            comunaEmpresa: formData.comunaEmpresa,
            ciudadEmpresa: formData.ciudadEmpresa,
        },
        logoBase64,
    });

    const generatePDF = (openInNewTab = false) => {
        const empty = countEmptyFields();
        const run = () => {
            generarFiniquitoPDF({ ...buildPayload(), openInNewTab });
            registrarLog({
                Usuario: cookies.usuario,
                Modulo: 'Finiquitos',
                Detalle: `Generación de finiquito: ${nombreCompleto()}`,
                Estado: 'Exito',
            });
        };
        if (empty > 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Campos incompletos',
                text: `Hay ${empty} campo(s) obligatorio(s) sin completar. ¿Desea generar el finiquito de todas formas?`,
                showCancelButton: true,
                confirmButtonText: 'Generar igual',
                cancelButtonText: 'Cancelar'
            }).then((result) => { if (result.isConfirmed) run(); });
        } else {
            run();
        }
    };

    // ==================== RENDER ====================
    return (
        <body id="page-top">
            <Sidebar
                imageUrl={imageUrl}
                isComponentsCollapsed={isComponentsCollapsed}
                toggleCollapse={toggleCollapse}
                setIsComponentsCollapsed={setIsComponentsCollapsed}
                isUtilitiesCollapsed={isUtilitiesCollapsed}
                setIsUtilitiesCollapsed={setIsUtilitiesCollapsed}
                isInventario={isInventario}
                setisIventatioCollapsed={setisIventatioCollapsed}
                isPrevencion={isPrevencion}
                setisPrevencionCollapsed={setisPrevencionCollapsed}
                isLogistica={isLogistica}
                setisLogisticaCollapsed={setisLogisticaCollapsed}
                isContabilidadCollapsed={isContabilidadCollapsed}
                setIsContabilidadCollapsed={setIsContabilidadCollapsed}
                isAdministracionCollapsed={isAdministracionCollapsed}
                setIsAdministracionCollapsed={setIsAdministracionCollapsed}
                cookies={cookies}
            />
            <div id="content-wrapper" className="d-flex flex-column">
                <div id="content">
                    <div className="container-fluid">
                        <div className="row">
                            {/* ============ PANEL IZQUIERDO: FORMULARIO ============ */}
                            <div className="col-lg-5 col-xl-4">
                                <div className="card shadow mb-4">
                                    <div className="card-header py-3 d-flex align-items-center justify-content-between">
                                        <h6 className="m-0 font-weight-bold text-primary">
                                            Módulo de Finiquitos de {empresaHeader}
                                        </h6>
                                        {countEmptyFields() > 0 && (
                                            <span className="badge bg-warning text-dark">{countEmptyFields()} campos pendientes</span>
                                        )}
                                    </div>
                                    <div className="card-body" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
                                        {/* Causal y fecha */}
                                        <AccordionSection id="causal" isOpen={openSection === 'causal'} onToggle={toggleSection} title="Causal y Fecha" icon="gavel">
                                            <FormInput label="Artículo" field="articuloFiniquito" options={articulosFiniquito} value={formData.articuloFiniquito} onChange={handleChange} />
                                            <FormInput label="En (comuna)" field="enComuna" placeholder="Ej: Concepción" value={formData.enComuna} onChange={handleChange} />
                                            <FormInput label="Fecha Finiquito" field="fechaFiniquito" type="date" value={formData.fechaFiniquito} onChange={handleChange} />
                                        </AccordionSection>

                                        {/* Trabajador */}
                                        <AccordionSection id="trabajador" isOpen={openSection === 'trabajador'} onToggle={toggleSection} title="Datos Trabajador" icon="person">
                                            <div className="input-group input-group-sm mb-2">
                                                <span className="input-group-text" style={{ minWidth: '150px', fontSize: '12px' }}>Precargar</span>
                                                <select className="form-select form-select-sm" value={idTrabajadorSeleccionado} onChange={handleSelectTrabajador}>
                                                    <option value="">— Seleccione trabajador —</option>
                                                    {trabajadores.map(t => (
                                                        <option key={t.ID_Trabajador} value={t.ID_Trabajador} data-rut={t.Rut}>
                                                            {t.NombreCompleto}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <FormInput label="Nombres" field="nombres" value={formData.nombres} onChange={handleChange} />
                                            <FormInput label="Apellidos" field="apellidos" value={formData.apellidos} onChange={handleChange} />
                                            <FormInput label="RUT" field="rutTrabajador" placeholder="12.345.678-9" value={formData.rutTrabajador} onChange={handleChange} />
                                            <FormInput label="Nacionalidad" field="nacionalidad" value={formData.nacionalidad} onChange={handleChange} />
                                            <FormInput label="Fecha Nacimiento" field="fechaNacimiento" type="date" value={formData.fechaNacimiento} onChange={handleChange} />
                                            <FormInput label="Domicilio" field="domicilioTrabajador" value={formData.domicilioTrabajador} onChange={handleChange} />
                                            <FormInput label="Comuna" field="comunaTrabajador" value={formData.comunaTrabajador} onChange={handleChange} />
                                            <FormInput label="Cargo" field="cargo" value={formData.cargo} onChange={handleChange} />
                                        </AccordionSection>

                                        {/* Vigencia */}
                                        <AccordionSection id="vigencia" isOpen={openSection === 'vigencia'} onToggle={toggleSection} title="Período del Contrato" icon="event">
                                            <FormInput label="Vigencia Desde" field="vigenciaDesde" type="date" value={formData.vigenciaDesde} onChange={handleChange} />
                                            <FormInput label="Vigencia Hasta" field="vigenciaHasta" type="date" value={formData.vigenciaHasta} onChange={handleChange} />
                                        </AccordionSection>

                                        {/* Montos */}
                                        <AccordionSection id="montos" isOpen={openSection === 'montos'} onToggle={toggleSection} title="Montos a Pagar" icon="payments">
                                            <FormInput label="Indemnización años" field="indemnizacion" placeholder="500.000" value={formData.indemnizacion} onChange={handleChange} />
                                            <FormInput label="Vacaciones prop." field="vacaciones" value={formData.vacaciones} onChange={handleChange} />
                                            <FormInput label="Faena u obras" field="faena" value={formData.faena} onChange={handleChange} />
                                            <FormInput label="Mes de aviso" field="mesAviso" value={formData.mesAviso} onChange={handleChange} />
                                            <div className="alert alert-info py-2 px-3 mt-2 mb-0" style={{ fontSize: '12px' }}>
                                                <strong>Total: </strong>${formatRemuneracion(totalMontos()) || '0'}
                                            </div>
                                        </AccordionSection>

                                        {/* Empresa */}
                                        <AccordionSection id="empresa" isOpen={openSection === 'empresa'} onToggle={toggleSection} title="Datos Empresa" icon="business">
                                            <FormInput label="Empresa" field="empresa" value={formData.empresa} onChange={handleChange} />
                                            <FormInput label="RUT Empresa" field="rutEmpresa" value={formData.rutEmpresa} onChange={handleChange} />
                                            <FormInput label="Rep. Legal" field="representanteLegal" value={formData.representanteLegal} onChange={handleChange} />
                                            <FormInput label="RUT Rep. Legal" field="rutRepresentante" value={formData.rutRepresentante} onChange={handleChange} />
                                            <FormInput label="Domicilio" field="domicilioEmpresa" value={formData.domicilioEmpresa} onChange={handleChange} />
                                            <FormInput label="Comuna" field="comunaEmpresa" value={formData.comunaEmpresa} onChange={handleChange} />
                                            <FormInput label="Ciudad" field="ciudadEmpresa" value={formData.ciudadEmpresa} onChange={handleChange} />
                                        </AccordionSection>

                                        {/* Botones de acción */}
                                        <div className="d-flex gap-2 mt-3 flex-wrap">
                                            <button className="btn btn-success flex-fill" onClick={() => generatePDF(false)}>
                                                <span className="material-symbols-rounded" style={{ fontSize: '16px', verticalAlign: 'middle', marginRight: '4px' }}>download</span>
                                                Descargar PDF
                                            </button>
                                            <button className="btn btn-primary flex-fill" onClick={() => generatePDF(true)}>
                                                <span className="material-symbols-rounded" style={{ fontSize: '16px', verticalAlign: 'middle', marginRight: '4px' }}>visibility</span>
                                                Vista previa
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* ============ PANEL DERECHO: VISTA PREVIA ============ */}
                            <div className="col-lg-7 col-xl-8">
                                <div className="card shadow mb-4">
                                    <div className="card-header py-3">
                                        <h6 className="m-0 font-weight-bold text-primary">Vista Previa del Finiquito</h6>
                                    </div>
                                    <div className="card-body" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', backgroundColor: '#e9ecef' }}>
                                        <div style={{
                                            backgroundColor: '#fff',
                                            padding: '50px 50px',
                                            maxWidth: '750px',
                                            margin: '0 auto',
                                            boxShadow: '0 2px 15px rgba(0,0,0,0.15)',
                                            fontFamily: 'Georgia, "Times New Roman", serif',
                                            fontSize: '12px',
                                            lineHeight: '1.6',
                                            color: '#222'
                                        }}>
                                            <h4 style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: '25px', letterSpacing: '2px' }}>
                                                FINIQUITO DE CONTRATO DE TRABAJO
                                            </h4>

                                            <p style={{ textAlign: 'justify' }}>
                                                En {renderText(formData.enComuna, "En Comuna")}, a {formData.fechaFiniquito ? formatearFechaTexto(formData.fechaFiniquito) : renderText("", "Fecha Finiquito")}, entre <strong>{formData.empresa || "[Empresa]"}</strong> R.U.T <strong>{formData.rutEmpresa || "[RUT Empresa]"}</strong> domiciliado en <strong>{formData.domicilioEmpresa || "[Domicilio Empresa]"}</strong> comuna de <strong>{formData.comunaEmpresa || "[Comuna Empresa]"}</strong> ciudad de <strong>{formData.ciudadEmpresa || "[Ciudad Empresa]"}</strong> representado por don(a) <strong>{formData.representanteLegal || "[Rep. Legal]"}</strong> C.I. <strong>{formData.rutRepresentante || "[RUT Rep.]"}</strong>, por una parte; y la otra, don <strong>{nombreCompleto() || "[Nombre Trabajador]"}</strong> cédula de identidad Nro. <strong>{formData.rutTrabajador || "[RUT]"}</strong>, de nacionalidad <strong>{formData.nacionalidad || "[Nacionalidad]"}</strong> nacido el <strong>{fechaDMY(formData.fechaNacimiento) || "[Fecha Nacimiento]"}</strong>, domiciliado en <strong>{formData.domicilioTrabajador || "[Domicilio]"}</strong> Comuna de <strong>{formData.comunaTrabajador || "[Comuna]"}</strong>, Ciudad de <strong>{formData.ciudadEmpresa || "[Ciudad]"}</strong>, en adelante, también el trabajador, se deja testimonio y se ha acordado el finiquito que consta de las siguientes cláusulas:
                                            </p>

                                            <p style={{ textAlign: 'justify' }}>
                                                <strong>PRIMERO: </strong>
                                                El trabajador prestó servicios al empleador en el cargo de <strong>{(formData.cargo || '').toUpperCase() || "[Cargo]"}</strong>, desde el <strong>{fechaDMY(formData.vigenciaDesde) || "[Desde]"}</strong> hasta el <strong>{fechaDMY(formData.vigenciaHasta) || "[Hasta]"}</strong>, fecha esta última en que su contrato de trabajo ha terminado de acuerdo con lo establecido en el <strong>{(formData.articuloFiniquito || '').toUpperCase() || "[Artículo]"}</strong>
                                            </p>

                                            <p style={{ textAlign: 'justify' }}>
                                                <strong>SEGUNDO: </strong>
                                                don <strong>{nombreCompleto() || "[Nombre]"}</strong>, declara recibir en este acto, a su entera satisfacción, de parte de <strong>{formData.empresa || "[Empresa]"}</strong> la suma de:
                                            </p>
                                            <ul style={{ marginLeft: '20px', listStyleType: 'none', paddingLeft: '15px' }}>
                                                <li>• Indemnización años de Servicio &nbsp;&nbsp; <strong>${formData.indemnizacion || '0'}</strong></li>
                                                <li>• Vacaciones Proporcionales &nbsp;&nbsp; <strong>${formData.vacaciones || '0'}</strong></li>
                                                <li>• Faena u Obras &nbsp;&nbsp; <strong>${formData.faena || '0'}</strong></li>
                                                <li>• Mes de Aviso &nbsp;&nbsp; <strong>${formData.mesAviso || '0'}</strong></li>
                                            </ul>
                                            <p style={{ textAlign: 'justify' }}>
                                                <strong>Son: ${formatRemuneracion(totalMontos()) || '0'}</strong>
                                                {totalEnLetras() && <> ({totalEnLetras()} pesos)</>}
                                            </p>
                                            <p style={{ textAlign: 'justify' }}>
                                                Don <strong>{nombreCompleto() || "[Nombre]"}</strong>, declara haber analizado y estudiado detenidamente dicha liquidación, aceptándola en todas sus partes, sin tener observación alguna que formularle.
                                            </p>

                                            <p style={{ textAlign: 'justify' }}>
                                                <strong>TERCERO: </strong>
                                                En consecuencia, el empleador no adeuda a don <strong>{nombreCompleto() || "[Nombre]"}</strong> ningún valor ni en especie ni en dinero, situación que el trabajador declara aceptar en este acto a su entera satisfacción.
                                            </p>

                                            <p style={{ textAlign: 'justify' }}>
                                                <strong>CUARTO: </strong>
                                                Don <strong>{nombreCompleto() || "[Nombre]"}</strong>, deja constancia que durante el tiempo que prestó servicios a <strong>{formData.empresa || "[Empresa]"}</strong>, recibió oportunamente el total de las remuneraciones, beneficios y demás prestaciones convenidas de acuerdo a su contrato de trabajo, clase de trabajo ejecutado y disposiciones legales pertinentes, y que en tal virtud el empleador nada le adeuda por tales conceptos.
                                            </p>

                                            <p style={{ textAlign: 'justify' }}>
                                                <strong>QUINTO: </strong>
                                                En virtud de lo anteriormente expuesto, don <strong>{nombreCompleto() || "[Nombre]"}</strong> manifiesta expresamente que <strong>{formData.empresa || "[Empresa]"}</strong> nada le adeuda en relación con los servicios prestados, otorgando el más amplio, completo, total y definitivo finiquito.
                                            </p>

                                            <p style={{ textAlign: 'justify' }}>
                                                <strong>SEXTO: </strong>
                                                Asimismo, declara el trabajador que, en todo caso, y a todo evento, renuncia expresamente a cualquier derecho, acción o reclamo que eventualmente tuviere o pudiere corresponderle en contra del empleador.
                                            </p>

                                            <p style={{ textAlign: 'justify' }}>
                                                <strong>SÉPTIMO: </strong>
                                                De acuerdo con la ley 21.389, el empleador declara que no ha recibido notificación judicial que implique retención alguna por concepto de pensión de alimentos.
                                            </p>

                                            {/* Firmas */}
                                            <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '60px' }}>
                                                <div style={{ textAlign: 'center', width: '40%' }}>
                                                    <div style={{ borderBottom: '1px solid #000', marginBottom: '8px', height: '40px' }}></div>
                                                    <strong>{nombreCompleto() || "[Nombre Trabajador]"}</strong>
                                                    <div style={{ fontSize: '10px', color: '#666' }}>TRABAJADOR</div>
                                                    <div style={{ fontSize: '10px', color: '#666' }}>RUT {formData.rutTrabajador || "[RUT]"}</div>
                                                </div>
                                                <div style={{ textAlign: 'center', width: '40%' }}>
                                                    <div style={{ borderBottom: '1px solid #000', marginBottom: '8px', height: '40px' }}></div>
                                                    <strong>{formData.empresa || "[Empresa]"}</strong>
                                                    <div style={{ fontSize: '10px', color: '#666' }}>EMPLEADOR</div>
                                                    <div style={{ fontSize: '10px', color: '#666' }}>RUT {formData.rutEmpresa || "[RUT Empresa]"}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </body>
    );
}

export default Finiquitos;
